import { APIClient } from "../helpers/api_helper";
import { ApiResponse } from "../types/api";

const api = new APIClient();
const RESOURCE = "/waiting-room";

export type WaitingRoomStatus = "waiting" | "called" | "in_service";

export interface WaitingRoomEntry {
  id: number;
  position?: number | null;
  unit_id?: number;
  professional_id?: number;
  client_id?: number;
  service_id?: number | null;
  status?: string;
  start_datetime?: string;
  client_arrived_at?: string | null;
  waiting_room_status: WaitingRoomStatus | null;
  waiting_room_called_at?: string | null;
  waiting_room_started_at?: string | null;
  wait_seconds: number;
  client?: {
    id?: number;
    name?: string;
    first_name?: string;
  } | null;
  professional?: {
    id?: number;
    name?: string;
    first_name?: string;
  } | null;
  service?: {
    id: number;
    name: string;
  } | null;
  unit?: {
    id: number;
    name: string;
  } | null;
}

export interface WaitingRoomListMeta {
  unit_id: number;
  date: string;
  waiting_count?: number;
  total: number;
}

export interface WaitingRoomListResponse extends ApiResponse<WaitingRoomEntry[]> {
  meta?: WaitingRoomListMeta;
}

export interface WaitingRoomListParams {
  unit_id: number;
  date?: string;
}

export const listWaitingRoom = (
  params: WaitingRoomListParams
): Promise<WaitingRoomListResponse> =>
  api.get<WaitingRoomListResponse>(RESOURCE, params);

export const listWaitingRoomDisplay = (
  params: WaitingRoomListParams
): Promise<WaitingRoomListResponse> =>
  api.get<WaitingRoomListResponse>(`${RESOURCE}/display`, params);

export const callNext = (
  params: WaitingRoomListParams
): Promise<ApiResponse<WaitingRoomEntry>> =>
  api.create<ApiResponse<WaitingRoomEntry>>(`${RESOURCE}/call-next`, params);

export const callEntry = (
  appointmentId: number
): Promise<ApiResponse<WaitingRoomEntry>> =>
  api.create<ApiResponse<WaitingRoomEntry>>(
    `${RESOURCE}/${appointmentId}/call`,
    {}
  );

export const startService = (
  appointmentId: number,
  payload?: { acknowledge_clinical_gate?: boolean }
): Promise<ApiResponse<WaitingRoomEntry>> =>
  api.create<ApiResponse<WaitingRoomEntry>>(
    `${RESOURCE}/${appointmentId}/start-service`,
    payload ?? {}
  );

export const dismissEntry = (
  appointmentId: number
): Promise<ApiResponse<WaitingRoomEntry>> =>
  api.create<ApiResponse<WaitingRoomEntry>>(
    `${RESOURCE}/${appointmentId}/dismiss`,
    {}
  );

export const moveEntry = (
  appointmentId: number,
  direction: "up" | "down"
): Promise<ApiResponse<WaitingRoomEntry>> =>
  api.create<ApiResponse<WaitingRoomEntry>>(
    `${RESOURCE}/${appointmentId}/move`,
    { direction }
  );

export const formatWaitDuration = (waitSeconds: number): string => {
  const total = Math.max(0, Math.floor(waitSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  }

  if (minutes < 1) {
    return "<1m";
  }

  return `${minutes}m`;
};
